import { useEffect } from 'react'
import { useStore } from '@/models'
import { refreshAccessToken } from '@/models/session/refreshToken'
import * as AuthSession from 'expo-auth-session'
import { useSessionStore } from '@/models/session/useSessionStore'

export function useTokenRefreshScheduler() {
  const rootStore = useStore()
  // Only track issuedAt to know when a NEW token is set (not when same token updates)
  const tokenIssuedAt = useSessionStore((state) => state.tokenConfig?.issuedAt)
  const hasRehydrated = useSessionStore((state) => state.hasRehydrated)

  useEffect(() => {
    // Wait for store rehydration before scheduling
    if (!hasRehydrated) {
      return
    }

    // Get latest token config from store (don't use from subscription)
    const tokenConfig = useSessionStore.getState().tokenConfig

    if (!tokenConfig?.refreshToken) {
      return
    }
    if (!tokenConfig?.issuedAt) {
      return
    }
    if (!tokenConfig?.expiresIn) {
      return
    }

    // Calculate time until token expires (refresh 5 minutes before)
    const expiresAt = (tokenConfig.issuedAt + tokenConfig.expiresIn) * 1000
    const now = Date.now()
    const refreshAt = expiresAt - 5 * 60 * 1000 // 5 minutes before expiry
    const delay = Math.max(0, refreshAt - now)

    if (__DEV__) {
      console.log('useTokenRefreshScheduler: Scheduling token refresh', {
        expiresAt: new Date(expiresAt).toISOString(),
        refreshAt: new Date(refreshAt).toISOString(),
        delayMs: delay,
        delayMinutes: Math.round(delay / 1000 / 60)
      })
    }

    if (delay === 0) {
      // Token already expired or about to expire - refresh immediately
      refreshAccessToken(
        JSON.parse(JSON.stringify(tokenConfig)) as AuthSession.TokenResponseConfig,
        rootStore
      ).catch((error) => {
        if (__DEV__) {
          console.error('useTokenRefreshScheduler: Immediate refresh failed', error)
        }
      })
      return
    }

    // Schedule refresh
    const timeoutId = setTimeout(() => {
      refreshAccessToken(
        JSON.parse(JSON.stringify(tokenConfig)) as AuthSession.TokenResponseConfig,
        rootStore
      ).catch((error) => {
        if (__DEV__) {
          console.error('useTokenRefreshScheduler: Scheduled refresh failed', error)
        }
      })
    }, delay)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [tokenIssuedAt, rootStore, hasRehydrated])
}
